import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth.store'
import { useChatStore } from '@/stores/chat-store'
import type { LoginCredentials, RegisterCredentials } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

export function useAuth() {
  const {
    user,
    token,
    isAuthenticated,
    isInitialized,
    setAuth,
    updateUser,
    clearAuth,
    setSessionError,
  } = useAuthStore()
  const queryClient = useQueryClient()

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: (data) => {
      setAuth(data.user, data.access_token)
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const registerMutation = useMutation({
    mutationFn: (credentials: RegisterCredentials) => authService.register(credentials),
    onSuccess: (data) => {
      setAuth(data.user, data.access_token)
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await authService.logout()
      } catch (error) {
        if (useAuthStore.getState().isAuthenticated) {
          setSessionError(
            "We couldn't securely log you out. Your session is still active; check your connection and try again.",
          )
        }
        throw error
      }
      clearAuth()
      queryClient.clear()
      useChatStore.getState().reset()
    },
  })

  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user'],
    queryFn: () => authService.getCurrentUser(),
    enabled: isAuthenticated && !!token,
    retry: false,
  })

  // Login and OTP token responses intentionally contain only a minimal user.
  // Keep the shared store in sync with /auth/me so organization-scoped hooks
  // can start immediately without requiring a full-page session restore.
  useEffect(() => {
    if (currentUser && currentUser !== user) updateUser(currentUser)
  }, [currentUser, updateUser, user])

  return {
    user: currentUser || user,
    isAuthenticated,
    isLoading:
      !isInitialized || loginMutation.isPending || registerMutation.isPending || isLoadingUser,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
    logoutError: logoutMutation.error,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  }
}
